import React from 'react';
import {
	Card,
	Form,
	Input,
	Button,
	List,
	Tag,
	notification,
	Select,
	Spin,
	Typography,
	Modal,
	Tooltip,
} from 'antd';
import {
	DeleteOutlined,
	CopyOutlined,
	TagOutlined,
	FileTextOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
	StopOutlined,
	LinkOutlined,
	PlusOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
	getWebhooks,
	createWebhook,
	deleteWebhook,
	updateWebhook,
	updateTutorialStepStatus,
} from '@/api/reseller';
import { Webhook, WebhookEvent, WebhookType } from '@/types/TReseller';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WebhookPayloadPreviewer } from './WebhookPayloadPreview';
import { webhookEvents } from './data/webhookEvents';
import { BookOpenIcon, BracesIcon, WebhookIcon } from 'lucide-react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import TutorialsModal from '../TutorialsModal';

const { Option } = Select;
const { Title, Text } = Typography;

const ALL_EVENTS = 'all_events';
const SLACK_GUIDE_URL = 'https://api.slack.com/messaging/webhooks#getting_started';
const SLACK_WEBHOOK_REGEX =
	/^https:\/\/hooks\.slack\.com\/services\/T[A-Z0-9]{8,10}\/B[A-Z0-9]{8,10}\/[A-Za-z0-9]{24}$/;
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<WebhookIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Connect Zapier webhook',
		description: 'Create Webhooks in your Admin Dashboard - Zapier example',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/create-webhooks-in-your-admin-dashboard-zapier-example',
	},
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<WebhookIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Connect Make webhook',
		description: 'Create Webhooks in your Admin Dashboard - Make example',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/create-webhooks-in-your-admin-dashboard-make-example',
	},
];

const ResellerWebhooks: React.FC = () => {
	const [form] = Form.useForm();
	const queryClient = useQueryClient();
	const currentReseller = useAppSelector(getResellerData);

	const [selectedEvents, setSelectedEvents] = React.useState<string[]>([]);
	const [selectedType, setSelectedType] = React.useState<WebhookType | undefined>(undefined);
	const [payloadModalOpen, setPayloadModalOpen] = React.useState(false);
	const [createModalOpen, setCreateModalOpen] = React.useState(false);
	const [editingWebhook, setEditingWebhook] = React.useState<Webhook | null>(null);

	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = React.useState(false);

	const [deletingWebhookId, setDeletingWebhookId] = React.useState<string | null>(null);
	const [togglingWebhookId, setTogglingWebhookId] = React.useState<string | null>(null);

	const eventOptions = [
		{ value: ALL_EVENTS, label: 'All Events' },
		{ value: WebhookEvent.CLIENT_CREATED, label: 'Client Created' },
		{ value: WebhookEvent.CLIENT_PAYMENT_FAILED, label: 'Client Payment Failed' },
		{ value: WebhookEvent.CLIENT_CHURNED, label: 'Client Churned' },
		{ value: WebhookEvent.TRIAL_ACCOUNT_CONVERTED, label: 'Trial Account Converted' },
		{ value: WebhookEvent.CALL_MADE, label: 'Call Made' },
		{ value: WebhookEvent.TEXT_MADE, label: 'Text Made' },
		// { value: WebhookEvent.USAGE_LIMITS_EXCEEDED, label: 'Usage Limits Exceeded' },
	];

	const { data: webhooksResponse, isPending: isLoadingWebhooks } = useQuery({
		queryKey: ['webhooks'],
		queryFn: getWebhooks,
		select: (response) => (response.success ? response.data || [] : []),
	});

	const createWebhookMutation = useMutation({
		mutationFn: createWebhook,
		onSuccess: () => {
			notification.success({
				message: 'Webhook Created',
				description: 'The webhook has been successfully created.',
			});
			form.resetFields();
			queryClient.invalidateQueries({ queryKey: ['webhooks'] });

			// Close modal and reset form after successful submission
			setCreateModalOpen(false);
			setSelectedEvents([]);
			setSelectedType(undefined);
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to Create Webhook',
				description: error.message || 'An error occurred while creating the webhook.',
			});
		},
	});

	const deleteWebhookMutation = useMutation({
		mutationFn: deleteWebhook,
		onSuccess: () => {
			notification.success({
				message: 'Webhook Deleted',
				description: 'The webhook has been successfully deleted.',
			});
			queryClient.invalidateQueries({ queryKey: ['webhooks'] });
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to Delete Webhook',
				description: error.message || 'An error occurred while deleting the webhook.',
			});
		},
	});

	const updateWebhookMutation = useMutation({
		mutationFn: (params: { webhookId: string; payload: Partial<Webhook> }) =>
			updateWebhook(params.webhookId, params.payload),
		onSuccess: () => {
			notification.success({
				message: 'Webhook Updated',
				description: 'The webhook has been successfully updated.',
			});
			queryClient.invalidateQueries({ queryKey: ['webhooks'] });

			// Close modal and reset form after successful update
			setCreateModalOpen(false);
			setEditingWebhook(null);
			form.resetFields();
			setSelectedEvents([]);
			setSelectedType(undefined);
		},
		onError: (error: Error) => {
			console.error('Failed to update webhook:', error);
			notification.error({
				message: 'Failed to Update Webhook',
				description: error.message || 'An error occurred while updating the webhook.',
			});
		},
	});

	const handleSubmit = async (values: any) => {
		const events = values.events.includes(ALL_EVENTS) ? Object.values(WebhookEvent) : values.events;

		if (editingWebhook) {
			updateWebhookMutation.mutate({
				webhookId: editingWebhook.id,
				payload: {
					...editingWebhook,
					...values,
					events,
				},
			});
		} else {
			createWebhookMutation.mutate({
				...values,
				events,
			});
		}
	};

	const handleEdit = (webhook: Webhook) => {
		setEditingWebhook(webhook);
		setSelectedType(webhook.type);
		setSelectedEvents(webhook.events || []);
		form.setFieldsValue({
			type: webhook.type,
			title: webhook.title,
			url: webhook.url,
			events: webhook.events,
			description: webhook.description,
		});
		setCreateModalOpen(true);
	};

	const handleModalClose = () => {
		setCreateModalOpen(false);
		setEditingWebhook(null);
		form.resetFields();
		setSelectedEvents([]);
		setSelectedType(undefined);
	};

	const handleDelete = (webhookId: string) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this webhook?',
			icon: <ExclamationCircleOutlined />,
			content: 'This action cannot be undone.',
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				setDeletingWebhookId(webhookId);
				deleteWebhookMutation.mutate(webhookId, {
					onSettled: () => setDeletingWebhookId(null),
				});
			},
		});
	};

	const handleToggleWebhookStatus = async (webhook: Webhook) => {
		const newStatus = webhook.status === 'active' ? 'inactive' : 'active';
		setTogglingWebhookId(webhook.id);
		updateWebhookMutation.mutate(
			{
				webhookId: webhook.id,
				payload: {
					...webhook,
					status: newStatus,
				},
			},
			{
				onSettled: () => setTogglingWebhookId(null),
			},
		);
	};

	const handleEventChange = (selectedEvents: string[]) => {
		const allEventsSelected = selectedEvents.includes(ALL_EVENTS);
		const otherEventsSelected = selectedEvents.filter((event) => event !== ALL_EVENTS);

		if (allEventsSelected) {
			// If "All Events" is selected, unselect all other events
			form.setFieldValue('events', [ALL_EVENTS]);
			setSelectedEvents([ALL_EVENTS]);
		} else if (otherEventsSelected.length > 0) {
			// If any other event is selected, unselect "All Events" if it was selected
			form.setFieldValue('events', otherEventsSelected);
			setSelectedEvents(otherEventsSelected);
		} else {
			form.setFieldValue('events', []);
			setSelectedEvents([]);
		}
	};

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleTutorialClick = () => {
		if (!currentReseller.tutorial_steps?.webhooks_tutorial) {
			updateTutorialMutation.mutate('webhooks_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.WEBHOOKS}>
			<div className="p-6">
				<div className="flex md:flex-row flex-col items-center justify-between gap-6 md:gap-12 mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Webhook for resellers
						</Title>
						<Text type="secondary">
							Webhooks let you monitor trigger events like account creation, payment failures, and
							more. You can connect to tools like Zapier, Make, or N8N for automation. Create
							onboarding campaigns, track usage via Slack, and much more.
						</Text>
					</div>
					<div className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							onClick={handleTutorialClick}
							className={
								!currentReseller.tutorial_steps?.webhooks_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
				</div>

				<Card
					title="Existing Webhooks"
					extra={
						<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
							Create Webhook
						</Button>
					}
				>
					<Spin spinning={isLoadingWebhooks}>
						<List
							dataSource={(webhooksResponse || [])
								.slice()
								.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())}
							renderItem={(webhook: Webhook) => (
								<List.Item className="!p-0 rounded-lg border border-gray-100 shadow mb-4">
									<div className="w-full">
										<div className="p-4 flex flex-col gap-1">
											<div className="flex items-center justify-between gap-2 w-full">
												<div className="text-lg font-semibold text-gray-900 truncate mb-1">
													{webhook.title}
												</div>
												<div className="flex items-center">
													<Tag
														color={webhook.type === 'slack' ? 'blue' : 'purple'}
														style={{ textTransform: 'uppercase', fontWeight: 500 }}
													>
														{webhook.type}
													</Tag>
													<Tag
														color={webhook.status === 'active' ? 'success' : 'default'}
														style={{ textTransform: 'uppercase', fontWeight: 500 }}
													>
														{webhook.status}
													</Tag>
												</div>
											</div>
											<div className="flex items-center gap-3 min-w-0 max-w-[90%]">
												<a
													href={webhook.url}
													target="_blank"
													rel="noopener noreferrer"
													className="text-base font-medium text-gray-900 flex items-center min-w-0 max-w-full"
												>
													<span className="truncate block">{webhook.url}</span>
												</a>
												<Button
													type="text"
													size="small"
													icon={<CopyOutlined />}
													onClick={() => {
														navigator.clipboard.writeText(webhook.url);
														notification.success({
															message: 'Copied!',
															description: 'Webhook URL copied to clipboard.',
														});
													}}
												/>
											</div>
										</div>

										<div className="p-4 flex flex-col gap-4 md:gap-2 text-sm">
											<div className="flex flex-col md:flex-row items-start md:items-center gap-2">
												<div className="flex items-center gap-2">
													<TagOutlined className="text-gray-400" />
													<Text type="secondary" className="!mb-0">
														Events:
													</Text>
												</div>
												<div className="flex flex-wrap">
													{webhook.events?.map((event: WebhookEvent) => (
														<Tag key={event} color="gold" className="capitalize">
															{event.replace(/_/g, ' ')}
														</Tag>
													))}
												</div>
											</div>
											{webhook.description && (
												<div className="flex flex-col md:flex-row items-start md:items-center gap-2">
													<div className="flex items-center gap-2">
														<FileTextOutlined className="text-gray-400" />
														<Text type="secondary" className="!mb-0">
															Description:
														</Text>
													</div>
													<Text className="text-gray-500">{webhook.description}</Text>
												</div>
											)}
											<div className="flex flex-col md:flex-row items-start md:items-center gap-2">
												<div className="flex items-center gap-2">
													<ClockCircleOutlined className="text-gray-400" />
													<Text type="secondary" className="!mb-0">
														Created:
													</Text>
												</div>
												<Text>{new Date(webhook.createdAt).toLocaleString()}</Text>
											</div>
										</div>

										<div className="px-4 py-2 bg-gray-50 flex justify-end items-center gap-4 overflow-x-auto">
											<Button
												type="text"
												onClick={() => handleToggleWebhookStatus(webhook)}
												icon={
													webhook.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />
												}
												loading={togglingWebhookId === webhook.id}
											>
												<span className="text-base !hidden md:!block">
													{webhook.status === 'active' ? 'Deactivate' : 'Activate'}
												</span>
											</Button>
											<Button
												type="text"
												icon={<EditOutlined />}
												onClick={() => handleEdit(webhook)}
											>
												<span className="text-base !hidden md:!block">Edit</span>
											</Button>
											<Button
												type="text"
												danger
												loading={deletingWebhookId === webhook.id}
												icon={<DeleteOutlined />}
												onClick={() => handleDelete(webhook.id)}
											>
												<span className="text-base !hidden md:!block">Delete</span>
											</Button>
										</div>
									</div>
								</List.Item>
							)}
						/>
					</Spin>
				</Card>

				<Modal
					title={
						<div className="flex flex-col">
							<span className="text-xl font-semibold">
								{editingWebhook ? 'Edit Webhook' : 'Create New Webhook'}
							</span>
							<span className="text-sm text-gray-500 mt-1">
								{editingWebhook
									? 'Update your webhook configuration'
									: 'Set up a new webhook to receive real-time notifications'}
							</span>
						</div>
					}
					open={createModalOpen}
					onCancel={handleModalClose}
					footer={null}
					width={800}
				>
					<Form form={form} layout="vertical" onFinish={handleSubmit}>
						<Form.Item
							name="type"
							label={
								<div className="flex items-center gap-2">
									<span className="font-semibold text-gray-900">Webhook Type</span>
									<span className="text-xs text-gray-500">
										(Choose how you want to receive notifications)
									</span>
								</div>
							}
							rules={[{ required: true, message: 'Please select webhook type' }]}
						>
							<Select
								placeholder="Select webhook type"
								onChange={(val) => setSelectedType(val)}
								value={selectedType}
								disabled={!!editingWebhook}
							>
								<Option value={WebhookType.SLACK}>Slack</Option>
								<Option value={WebhookType.API}>API</Option>
							</Select>
						</Form.Item>

						{selectedType === WebhookType.SLACK && (
							<div className="mb-4">
								<a
									href={SLACK_GUIDE_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:underline flex items-center text-sm font-medium"
								>
									<LinkOutlined className="mr-1" />
									Guide: How to set up Slack webhooks
								</a>
							</div>
						)}

						<Form.Item
							name="title"
							label={
								<div className="flex items-center gap-2">
									<span className="font-semibold text-gray-900">Webhook Title</span>
									<span className="text-xs text-gray-500">
										(A descriptive name for your webhook)
									</span>
								</div>
							}
							rules={[{ required: true, message: 'Please input webhook title' }]}
						>
							<Input placeholder="Enter webhook title" />
						</Form.Item>

						<Form.Item
							name="url"
							label={
								<div className="flex items-center gap-2">
									<span className="font-semibold text-gray-900">Webhook URL</span>
									<span className="text-xs text-gray-500">
										(The endpoint where notifications will be sent)
									</span>
								</div>
							}
							rules={[
								{ required: true, message: 'Please input webhook URL' },
								{
									validator: (_, value) => {
										if (!value) {
											return Promise.resolve();
										}
										if (selectedType === WebhookType.SLACK) {
											if (!SLACK_WEBHOOK_REGEX.test(value)) {
												return Promise.reject(
													'Invalid Slack webhook URL format. It should be like: https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX',
												);
											}
										} else if (selectedType === WebhookType.API) {
											if (!URL_REGEX.test(value)) {
												return Promise.reject(
													'Please enter a valid URL (e.g., https://www.example.com)',
												);
											}
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input
								placeholder={
									selectedType === WebhookType.SLACK
										? 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX'
										: 'Enter webhook URL'
								}
							/>
						</Form.Item>

						<Form.Item
							name="events"
							label={
								<div className="flex items-center justify-between w-full gap-4">
									<div className="flex items-center gap-2">
										<span className="font-semibold text-gray-900">Events</span>
										<span className="text-xs text-gray-500">
											(Select which events should trigger notifications)
										</span>
									</div>
									<Tooltip title="View event payload reference">
										<button
											type="button"
											className="text-blue-600 hover:text-blue-800 focus:outline-none flex items-center font-medium"
											onClick={() => setPayloadModalOpen(true)}
										>
											<BracesIcon size={14} className="mr-1 mt-0.5" />
											<span>View Payload Structure</span>
										</button>
									</Tooltip>
								</div>
							}
							rules={[{ required: true, message: 'Please select at least one event' }]}
						>
							<Select
								mode="multiple"
								placeholder="Select events"
								maxTagCount="responsive"
								onChange={handleEventChange}
								value={selectedEvents}
							>
								{eventOptions.map(({ value, label }) => (
									<Option
										key={value}
										value={value}
										disabled={value !== ALL_EVENTS && selectedEvents.includes(ALL_EVENTS)}
									>
										{label}
									</Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item
							name="description"
							label={
								<div className="flex items-center gap-2">
									<span className="font-semibold text-gray-900">Description</span>
									<span className="text-xs text-gray-500">
										(Optional: Add details about this webhook's purpose)
									</span>
								</div>
							}
						>
							<Input.TextArea placeholder="Enter webhook description" />
						</Form.Item>

						<Form.Item className="mb-0">
							<div className="flex justify-end gap-2">
								<Button onClick={handleModalClose}>Cancel</Button>
								<Button
									type="primary"
									htmlType="submit"
									loading={createWebhookMutation.isPending || updateWebhookMutation.isPending}
								>
									{editingWebhook ? 'Update Webhook' : 'Create Webhook'}
								</Button>
							</div>
						</Form.Item>
					</Form>
				</Modal>

				<Modal
					open={payloadModalOpen}
					onCancel={() => setPayloadModalOpen(false)}
					footer={null}
					width={800}
				>
					<WebhookPayloadPreviewer events={webhookEvents} />
				</Modal>
			</div>

			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			/>
		</ResellerDashboard>
	);
};

export default ResellerWebhooks;
